import React from "react";
import HeroMockup from "./HeroMockup";
import CTAButton from "./CTAButton";
import { useAuth } from "../../auth/AuthContext";

/**
 * Landing page hero, per design-system/landing.md §6 row 2. Dual CTA: the
 * primary drives signup (item 20: routes through /signup, not straight to
 * GitHub's install URL) unless the visitor is already signed in, in which
 * case it goes straight to their dashboard instead of re-running the signup
 * flow. The secondary is a same-page anchor rather than a second GitHub URL
 * — no reason to send an undecided visitor to GitHub twice.
 */
export default function Hero() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-6xl items-center px-6 md:px-8">
      <div className="grid w-full items-center gap-12 py-16 lg:grid-cols-2">
        <div>
          {/*
            Both halves are deliberately claims we cannot be wrong about. Earlier
            drafts failed on exactly that: "Reviewing it didn't [get faster]" is
            contestable — 1 in 5 GitHub reviews now involve an agent — and "AI
            can't decide who owns it" asserts a *capability* limit that's simply
            false, since gitStream and Aviator FlexReview already route reviewers
            by expertise. "A human still has to own it" is a claim about
            accountability, so no competitor's roadmap can invalidate it.

            Don't reintroduce "merge" here: Anthurium acts at PR-open and never
            touches the merge, and merge-gating is Graphite/Aviator's category.

            Each sentence is its own block so the line never breaks mid-sentence.
            The explicit {" "} matters: without it textContent runs the sentences
            together ("…the code.A human…") even though innerText looks right.

            text-balance (Tailwind's `text-wrap: balance`) is on each span, not
            the h1 — balancing is computed per box, and each sentence is its own
            box here. Without it, "AI reviews the code." greedy-wraps to "AI
            reviews the" / "code." at common desktop widths, orphaning "code."
            alone on its line. Balance redistributes to "AI reviews" / "the
            code." instead. Unsupported browsers just get the old greedy wrap —
            this is progressive enhancement, not a dependency.

            This H1 is duplicated in the generated share card. Change it here and
            you must also update og:title/twitter:title in public/index.html and
            rerun `npm run brand`. An e2e test asserts og:title equals this
            heading, so a missed step fails rather than shipping a stale card.
          */}
          <h1 className="mb-5 text-[clamp(2.5rem,5vw+1rem,4.5rem)] font-bold leading-[1.1] tracking-tight text-rf-text-primary">
            <span className="block text-balance">AI reviews the code.</span>{" "}
            <span className="block text-balance">A human still has to own it.</span>
          </h1>
          <p className="mb-8 max-w-[52ch] text-lg leading-relaxed text-rf-text-secondary">
            Anthurium scores every PR for complexity, flags what's critical,
            and assigns the teammate who's actually touched those files —
            before anyone opens the diff.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            {isAuthenticated ? (
              <CTAButton to="/dashboard">Go to dashboard →</CTAButton>
            ) : (
              <CTAButton to="/signup">Start free trial</CTAButton>
            )}
            <CTAButton variant="secondary" href="#how-it-works">
              See how it works
            </CTAButton>
          </div>
          {/*
            Deliberately does NOT name CodeRabbit/Copilot/Cursor here. We have no
            integration with any of them, and "works alongside <product>" in a hero
            reads as one. They're named only in /pricing's FAQ, where the question
            itself makes the framing comparative rather than integrative.
          */}
          <p className="mt-6 text-sm text-rf-text-muted">
            Runs alongside your AI reviewer, not instead of it — Anthurium never
            reads your code.
          </p>
        </div>
        <HeroMockup />
      </div>
    </div>
  );
}
